import React, { useEffect, useState } from "react";
import { Bell, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ensureSubscribed, pushSupported, pushPermission } from "../lib/pushNotifications";

// Sticky bottom opt-in bar for daily verse Web Push notifications.
// Shows on EVERY page (per user's pick — option B placement) until they
// tap Allow or Dismiss. Dismissal is sticky for 14 days via localStorage
// so we're not nagging; after 14 days we ask again (some users change
// their mind once they see what they're missing on the email side).
//
// Wording (per user's pick — option A):
//   "📲 Get the daily verse right on your phone — morning, midday,
//    and a Lord's Prayer at night. Free."
//
// Self-hides when:
//   • Browser doesn't support PushManager / Notification
//   • User has already granted permission (already subscribed)
//   • User dismissed within the last 14 days
//   • Server's VAPID key isn't configured (silent no-op)
const DISMISS_KEY = "verse_push_dismissed_at_v1";
const DISMISS_DAYS = 14;

export default function PushOptInBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    if (pushPermission() === "granted") return;
    if (pushPermission() === "denied") return; // can't re-prompt, browser locked it
    try {
      const at = window.localStorage.getItem(DISMISS_KEY);
      if (at) {
        const ageMs = Date.now() - parseInt(at, 10);
        if (Number.isFinite(ageMs) && ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
          return;
        }
      }
    } catch (_) {}
    // 1.5s delay so it doesn't slam in during the first paint.
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const handleAllow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await ensureSubscribed({ hour: 7 });
      try { window.localStorage.removeItem(DISMISS_KEY); } catch (_) {}
      toast.success("You're in. Morning verse, midday verse, and the Lord's Prayer at night — all coming.");
      setVisible(false);
    } catch (e) {
      // The most common failure path is "Permission denied" — give the
      // user an honest, actionable message instead of a stack trace.
      toast.error(e?.message || "Couldn't turn on notifications. Check your browser settings.");
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] px-3 pb-3 pointer-events-none animate-fade-up"
      data-testid="push-optin-banner"
    >
      <div className="mx-auto max-w-2xl pointer-events-auto bg-forest text-cream rounded-2xl shadow-2xl border border-cream/10 flex items-center gap-3 px-4 py-3">
        <div className="w-9 h-9 rounded-full bg-terracotta grid place-items-center shrink-0">
          <Bell size={18} weight="fill" className="text-cream" />
        </div>
        <div className="flex-1 min-w-0 text-sm leading-snug">
          <span className="font-semibold">📲 Get the daily verse right on your phone</span>{" "}
          <span className="text-cream/80">— morning, midday, and a Lord's Prayer at night. Free.</span>
        </div>
        <button
          type="button"
          onClick={handleAllow}
          disabled={busy}
          data-testid="push-optin-allow"
          className="shrink-0 px-4 py-2 text-terracotta hover:text-terracotta/80 text-xs font-bold tracking-wider uppercase hover:bg-terracotta-600 disabled:opacity-60"
        >
          {busy ? "..." : "Allow"}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="push-optin-dismiss"
          aria-label="Dismiss notification prompt"
          className="shrink-0 w-8 h-8 rounded-full grid place-items-center text-cream/70 hover:text-cream hover:bg-cream/10"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
